import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Bell, BellOff, Check, KeyRound, User } from "lucide-react";
import {
  changeMyPassword,
  updateProfile,
  type ProfileUpdate,
} from "../lib/agent";
import {
  getCurrentSubscription,
  isPushSupported,
  sendPushTest,
  subscribeForPush,
  unsubscribeFromPush,
} from "../lib/push";
import { Button, Card, Input } from "../components/ui";
import { fetchMe, getStoredUser, setStoredUser, type AuthUser } from "../lib/api";
import { AdminPreviewBanner, useIsAgent } from "../components/AdminPreviewBanner";
import { FieldHelp, InfoTip } from "../components/InfoTip";

export function AgentAccount() {
  const userIsAgent = useIsAgent();
  return (
    <main className="mx-auto w-full max-w-6xl gradient-radial-warm px-4 py-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Mein Konto</h2>
        <p className="mt-2 text-sm text-ink-muted">
          Stammdaten und Passwort ändern.
        </p>
      </div>

      {!userIsAgent && <AdminPreviewBanner pageName="Konto" />}

      {userIsAgent && (
        <>
          <ProfileForm />
          {/* Master-Modell: Prozente verwaltet der Master unter „Mein Team" —
              der alte Selbst-Einstell-Regler (SponsorShareCard) ist raus. */}
          <PasswordForm />
          <PushNotificationsCard />
        </>
      )}
    </main>
  );
}


function ProfileForm() {
  const stored = getStoredUser();
  const [me, setMe] = useState<AuthUser | null>(stored);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [addr, setAddr] = useState("");
  const [zip, setZip] = useState("");
  const [city, setCity] = useState("");
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Initial load aus /auth/me um aktuelle Daten zu haben
  useEffect(() => {
    fetchMe()
      .then((fresh) => {
        setMe(fresh);
        setStoredUser(fresh);
        const [f, ...rest] = (fresh.name || "").split(" ");
        setFirst(f || "");
        setLast(rest.join(" ") || "");
      })
      .catch(() => {});
    // Wir können Adresse/Phone nicht direkt aus /auth/me lesen (UserResponse hat sie nicht).
    // Das ist OK — User füllt die Felder ggf. neu. Backend nimmt nur exclude_none=True.
  }, []);

  const save = useMutation({
    mutationFn: (payload: ProfileUpdate) => updateProfile(payload),
    onSuccess: () => {
      setSavedAt(Date.now());
      window.setTimeout(() => setSavedAt(null), 2500);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: ProfileUpdate = {};
    if (first.trim()) payload.first_name = first.trim();
    if (last.trim()) payload.last_name = last.trim();
    if (phone.trim()) payload.phone = phone.trim();
    if (addr.trim()) payload.address_line1 = addr.trim();
    if (zip.trim()) payload.postal_code = zip.trim();
    if (city.trim()) payload.city = city.trim();
    save.mutate(payload);
  }

  return (
    <Card className="mb-4">
      <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
        <User className="size-4 text-accent-orange" />
        Stammdaten
        <InfoTip title="Was kann ich hier ändern?">
          <strong>Änderbar:</strong> Name, Telefon, Adresse — werden für
          Rechnungen/Gutschriften verwendet.
          <br />
          <br />
          <strong>Nicht änderbar:</strong> Email + Empfehlungs-Code (an
          Anmeldung gekoppelt) und IBAN/USt-ID (Admin-Review im
          Upgrade-Flow nötig — bei Änderung neu beantragen).
        </InfoTip>
      </h3>
      <p className="text-xs text-ink-muted mb-4">
        Email ({me?.email}) und Empfehlungs-Code ({me?.agent_code}) lassen sich nicht ändern.
        IBAN/USt-ID gehen über das Upgrade-Formular (Admin reviewt sie).
      </p>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Vorname">
            <Input value={first} onChange={(e) => setFirst(e.target.value)} />
          </Field>
          <Field label="Nachname">
            <Input value={last} onChange={(e) => setLast(e.target.value)} />
          </Field>
        </div>
        <Field label="Telefon">
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          <FieldHelp>
            Optional — falls der Admin dich bei Rückfragen erreichen will (wird
            nicht öffentlich angezeigt).
          </FieldHelp>
        </Field>
        <Field label="Straße + Nr.">
          <Input value={addr} onChange={(e) => setAddr(e.target.value)} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-1">
            <Field label="PLZ">
              <Input value={zip} onChange={(e) => setZip(e.target.value)} />
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Ort">
              <Input value={city} onChange={(e) => setCity(e.target.value)} />
            </Field>
          </div>
        </div>

        {save.error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
            {save.error instanceof Error ? save.error.message : "Fehler"}
          </div>
        )}
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? "Speichere…" : "Speichern"}
          </Button>
          {savedAt && (
            <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
              <Check className="size-3" /> Gespeichert
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

function PasswordForm() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [nextRepeat, setNextRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const change = useMutation({
    mutationFn: () => changeMyPassword(current, next),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setNextRepeat("");
      setError(null);
      setSavedAt(Date.now());
      window.setTimeout(() => setSavedAt(null), 3000);
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Fehler"),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (next.length < 8) {
      setError("Neues Passwort muss mindestens 8 Zeichen lang sein");
      return;
    }
    if (next !== nextRepeat) {
      setError("Wiederholung stimmt nicht überein");
      return;
    }
    change.mutate();
  }

  return (
    <Card>
      <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
        <KeyRound className="size-4 text-accent-orange" />
        Passwort ändern
      </h3>
      <p className="text-xs text-ink-muted mb-4">
        Du brauchst dein aktuelles Passwort um es zu ändern.
      </p>
      <form onSubmit={handleSubmit} className="space-y-3">
        <Field label="Aktuelles Passwort">
          <Input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            autoComplete="current-password"
          />
        </Field>
        <Field label="Neues Passwort (mind. 8 Zeichen)">
          <Input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            autoComplete="new-password"
            minLength={8}
          />
        </Field>
        <Field label="Wiederholen">
          <Input
            type="password"
            value={nextRepeat}
            onChange={(e) => setNextRepeat(e.target.value)}
            required
            autoComplete="new-password"
          />
        </Field>

        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
            {error}
          </div>
        )}
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={change.isPending || !current || !next}>
            {change.isPending ? "Ändere…" : "Passwort ändern"}
          </Button>
          {savedAt && (
            <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
              <Check className="size-3" /> Geändert
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-muted">
        {label}
      </span>
      {children}
    </label>
  );
}

function PushNotificationsCard() {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (!isPushSupported()) {
      setSupported(false);
      return;
    }
    setSupported(true);
    getCurrentSubscription().then((s) => setSubscribed(!!s));
  }, []);

  const onSubscribe = async () => {
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      await subscribeForPush();
      setSubscribed(true);
      setInfo("Aktiv. Du bekommst ab jetzt Push-Nachrichten.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Fehler");
    } finally {
      setBusy(false);
    }
  };

  const onUnsubscribe = async () => {
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
      setInfo("Push deaktiviert.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Fehler");
    } finally {
      setBusy(false);
    }
  };

  const onTest = async () => {
    setError(null);
    setInfo(null);
    try {
      const res = await sendPushTest();
      setInfo(`Test gesendet (${res.sent} Geraet${res.sent === 1 ? "" : "e"}).`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Fehler");
    }
  };

  if (!supported) {
    return (
      <Card className="mt-4 bg-cream-soft/40">
        <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
          <BellOff className="size-4 text-ink-muted" />
          Push-Benachrichtigungen
        </h3>
        <p className="text-xs text-ink-muted">
          Dein Browser unterstuetzt keine Push-Nachrichten. Auf iPhone/iPad funktioniert es nur
          wenn du die App ueber Safari → „Zum Home-Bildschirm" installiert hast.
        </p>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
        <Bell className="size-4 text-accent-orange" />
        Push-Benachrichtigungen
      </h3>
      <p className="text-xs text-ink-muted mb-3">
        Lass dich benachrichtigen wenn du eine neue Provision verdient hast oder das Team auf
        eine Idee von dir geantwortet hat.
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        {!subscribed ? (
          <Button onClick={onSubscribe} disabled={busy}>
            <Bell className="size-4" />
            {busy ? "Frage…" : "Push aktivieren"}
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onUnsubscribe} disabled={busy}>
              <BellOff className="size-4" />
              {busy ? "…" : "Deaktivieren"}
            </Button>
            <Button variant="ghost" onClick={onTest}>
              Test senden
            </Button>
            <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
              <Check className="size-3.5" /> aktiv
            </span>
          </>
        )}
      </div>
      {info && (
        <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs text-emerald-900">
          {info}
        </div>
      )}
      {error && (
        <div className="mt-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-1.5 text-xs text-rose-800">
          {error}
        </div>
      )}
    </Card>
  );
}
